import { Injectable, OnDestroy } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { BehaviorSubject, Observable, Subject, of, switchMap, takeUntil } from "rxjs";
import { ProductSalesResponse } from "src/app/models/Sales/ProductSalesResponse";
import { SalesByCardResume } from "src/app/models/Sales/SalesByCardResume.model";
import { SalesResponse } from "src/app/models/Sales/SalesResponse.model";
import { SalesResume, salesPaymentMethod } from "src/app/models/Sales/SalesResume.model";
import { SalesResumeTable } from "src/app/models/Sales/SalesResumeTable.model";
import { ApiService } from "../api/api.service";
import { SalesByCardResponse } from "src/app/models/Sales/SalesByCardResponse.model";
import { CardFlag } from "src/app/models/CardFlag.model";
import { SaleCanceledResponse } from "src/app/models/Sales/SaleCanceledResponse";
import { TransactionEstornoResponse } from "src/app/models/Sales/TransactionEstornoResponse";
import { SalesByProductResponse } from "src/app/models/Sales/SalesByProductResponse";
import { SalesByProductResume } from "src/app/models/Sales/SalesByProductResume";
import { ExcelConfigService } from "../export-excel/excel-export.service";
import { ExcelExportService } from "../export-excel/excel-configuration.service";
import { ConfigurationOptionsPriceFormationEnum } from 'src/app/Enums/ConfigurationOptionsPriceFormationEnum';
import { ConfigurationsEnum } from 'src/app/Enums/ConfigurationsEnum';
import { WizardProductService } from '../product/wizard/wizard-product.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root'
})
export class SalesService implements OnDestroy {

    constructor(
        private api: ApiService,
        private snackBar: MatSnackBar, 
        private excelConfigService: ExcelConfigService,
        private wizardProductService: WizardProductService, // injetar serviço para buscar configs
        private translateService: TranslateService
    ) {
    }

    ngOnDestroy(): void {
        this.destroyedListSales.next();
        this.destroyedListSales.complete();
        this._listSales.unsubscribe();
        this.destroyedListSalesByCard.next();
        this.destroyedListSalesByCard.complete();
        this._listSalesByCard.unsubscribe();
        this.destroyedListSalesbyProduct.next();
        this.destroyedListSalesbyProduct.complete();
        this._listSalesByProduct.unsubscribe();
    }

    //sales with products
    private _listSales: BehaviorSubject<SalesResume[]> = new BehaviorSubject<SalesResume[]>(Object.assign({}));
    setSales(product: SalesResume[]) {
        this._listSales.next(product);
    }

    getSales(): Observable<SalesResume[]> {
        return this._listSales.asObservable();
    }

    // Falha na última consulta do relatório de vendas: a tela mostra o erro com "Tentar novamente" em vez de tabela vazia
    private _erroNaConsultaDeVendas = new BehaviorSubject<boolean>(false);
    getErroNaConsultaDeVendas(): Observable<boolean> {
        return this._erroNaConsultaDeVendas.asObservable();
    }

    private _listSalesByCard: BehaviorSubject<SalesByCardResume[]> = new BehaviorSubject<SalesByCardResume[]>(Object.assign({}));
    setSalesByCard(product: SalesByCardResume[]) {
        this._listSalesByCard.next(product);
    }

    getSalesByCard(): Observable<SalesByCardResume[]> {
        return this._listSalesByCard.asObservable();
    }

    async getCardFlags(): Promise<CardFlag[]> {
        const r = await this.api.get<CardFlag[]>('/v1/smart-pos/card-flags')
        if (r.success) {
            return r.data;
        } else { throw new Error(r.message) }
    }

    private _listSalesByProduct: BehaviorSubject<SalesByProductResume[]> = new BehaviorSubject<SalesByProductResume[]>(Object.assign({}));
    setSalesByProduct(product: SalesByProductResume[]) {
        this._listSalesByProduct.next(product);
    }

    getSalesByProduct(): Observable<SalesByProductResume[]> {
        return this._listSalesByProduct.asObservable();
    }

    private _totalSales: number = 0;
    private set setTotalSales(sales: number) {
        this._totalSales = sales;
    }
    get totalSales(): number {
        return this._totalSales;
    }
    //number of total sales
    private _totalSalesAmount: number = 0;
    private set settotalSalesAmount(salesAmount: number) {
        this._totalSalesAmount = salesAmount;
    }
    get totalSalesAmount(): number {
        return this._totalSalesAmount;
    }

    //sales total value
    get totalValue(): number {
        return this._listSales.getValue().reduce((total: number, current) => {
            return total + (current.total ?? 0);
        }, 0);
    }

    get totalByCardValue(): number {
        return this._listSalesByCard.getValue().reduce((total: number, current) => {
            return total + (current.total ?? 0);
        }, 0);
    }

    //number of total sales by card
    private _totalSalesByCard: number = 0;
    private set setTotalSalesByCard(sales: number) {
        this._totalSalesByCard = sales;
    }
    get totalSalesByCard(): number {
        return this._totalSalesByCard;
    }

    //number of total sales by product
    private _totalSalesByProduct: number = 0;
    private set setTotalSalesByProduct(sales: number) {
        this._totalSalesByProduct = sales;
    }
    get totalSalesByProduct(): number {
        return this._totalSalesByProduct;
    }

    destroyedListSales = new Subject<void>();
    destroyedListSalesByCard = new Subject<void>();
    destroyedListSalesbyProduct = new Subject<void>();

    public getListSales(currentPage: number, limitPerPage: number, startDate?: Date, endDate?: Date, paymentMethod?: number, pdvId?: number, canceled?: boolean, loginId?: number) {
        const filtros: any = {
            currentPage,
            limitPerPage,
        };
        if (startDate && endDate) {
            startDate.setHours(0, 0, 0, 0);
            filtros['startDate'] = startDate.toISOString();
            endDate.setHours(23, 59, 59, 999);
            filtros['endDate'] = endDate.toISOString();
        }
        if (paymentMethod != undefined && paymentMethod > 0) {
            filtros['paymentMethod'] = paymentMethod;
        }

        filtros['pdvId'] = pdvId;
        filtros['canceled'] = canceled;
        filtros['loginId'] = loginId;

        of(this.api.get<SalesResponse>('/v1/sales/all-sales', filtros)).pipe(
            takeUntil(this.destroyedListSales),
            switchMap(sales => sales.then((sale) => {
                if (!sale?.success) {
                    sale?.message ?? this.warningError(sale?.message!)
                    this.setTotalSales = 0;
                    this.settotalSalesAmount = 0;
                    this.setSales([]);
                    this._erroNaConsultaDeVendas.next(true);
                    return;
                }
                this._erroNaConsultaDeVendas.next(false);
                this.setTotalSales = sale.data.totalSales;
                this.settotalSalesAmount = sale.data.totalSalesAmount;
                const responseSales: SalesResume[] = [...sale.data.allSales];
                this.setSales(responseSales);
            }))
        ).subscribe();
    }

    private toLocalDayBounds(startDate: string, endDate: string): { startDate: string, endDate: string } {
        const [startDay, startMonth, startYear] = startDate.split('/').map(Number);
        const [endDay, endMonth, endYear] = endDate.split('/').map(Number);
        return {
            startDate: new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0).toISOString(),
            endDate: new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999).toISOString()
        };
    }

    public getListSalesByCard(currentPage: number, limitPerPage: number, startDate?: string, endDate?: string, paymentMethod?: number, flagId?: number, status?: number) {
        const filtros: any = {
            currentPage,
            limitPerPage,
        };
        if (startDate && endDate) {
            Object.assign(filtros, this.toLocalDayBounds(startDate, endDate));
        }
        if (paymentMethod != undefined && paymentMethod > 0) {
            filtros['paymentMethod'] = paymentMethod;
        }
        if (flagId != undefined && flagId >= 0) {
            filtros['flagId'] = flagId;
        }
        if (status != undefined && status >= 0) {
            filtros['status'] = status;
        }

        of(this.api.get<SalesByCardResponse>('/v1/sales/all-sales-by-card', filtros)).pipe(
            takeUntil(this.destroyedListSalesByCard),
            switchMap(salesByCard => salesByCard.then((saleByCard) => {
                if (!saleByCard.success) {
                    saleByCard?.message ?? this.warningError(saleByCard.message!)
                    return;
                }
                this.setTotalSalesByCard = saleByCard.data.totalSales;//total salesByCards for pagination
                const responseSales: SalesByCardResume[] = [...saleByCard.data.allSales];
                this.setSalesByCard(responseSales);
            }))
        ).subscribe();
    }
    
    private warningError(message: string) {
        this.snackBar.open(message, '', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['warning-snackbar']
        });
    }

    public async getSalesProduct(salesId: number[]): Promise<ProductSalesResponse[]> {
        const r = await this.api.get<ProductSalesResponse[]>('/v1/sales/product-sales', { salesId: salesId })
        if (r.success)
            return r.data

        console.error(r.message);
        return [];
    }
    
    public async getSaleById(saleId: number): Promise<SalesResumeTable | undefined> {
        const r = await this.api.get<SalesResumeTable | undefined>(`/v1/sales/${saleId}`);
        if (r.success && r.data)
            return r.data
        //Por padrão o ts já seta a venda como unfedined por ela ter a interrogação
        //portanto caso a api não retorne a venda ela continuará como undefined
        return undefined;
    }
    
    public async getSaleCanceled(saleId: number): Promise<SaleCanceledResponse[]> {
        const r = await this.api.get<SaleCanceledResponse[]>(`/v1/sales/sales-canceled/${saleId}`)
        if (r.success)
            return Array.isArray(r.data) ? r.data : [r.data];
        
        console.error(r.message);
        return [];
    }
    
    public async getEstornoTransaction(cardTransactionId: number): Promise<TransactionEstornoResponse[]> {
        const r = await this.api.get<TransactionEstornoResponse[]>(`/v1/smart-pos/estorno-transaction/${cardTransactionId}`)
        if (r.success)
            return r.data
        
        console.error(r.message);
        return [];
    }
    
    public async getListSalesByProducts(
        startDate?: string,
        endDate?: string
    ) {
        const filtros: any = {};

        if (startDate && endDate) {
            Object.assign(filtros, this.toLocalDayBounds(startDate, endDate));
        }

        of(this.api.get<SalesByProductResponse>('/v1/sales/all-sales-by-product', filtros)).pipe(
            takeUntil(this.destroyedListSalesbyProduct),
            switchMap(salesByProduct => salesByProduct.then((saleByProduct) => {
                if (!saleByProduct.success) {
                    saleByProduct?.message ?? this.warningError(saleByProduct.message!);
                    this.setSalesByProduct([]);
                    this.setTotalSalesByProduct = 0;
                    return;
                }

                this.setTotalSalesByProduct = saleByProduct.data.totalRecords;
                const responseSales: SalesByProductResume[] = [...saleByProduct.data.items];
                this.setSalesByProduct(responseSales);
            }))
        ).subscribe();
    }

    public async exportSalesByProductToExcel(fileName: string = 'vendas_por_produto', enrichedData?: any[], totals?: any): Promise<void> {
        const salesByProductData = enrichedData || this._listSalesByProduct.getValue();
        const marginOrMarkupLabel = await this.getCurrentMarginOrMarkupLabel();
        const headers = [
            { key: 'productCode', label: 'Código do Produto', type: 'text' as const },
            { key: 'name', label: 'Nome do Produto', type: 'text' as const },
            { key: 'quantity', label: 'Qtde Vendida', type: 'number' as const, decimals: 3 },
            { key: 'currentCostPrice', label: 'Preço Custo Atual', type: 'currency' as const, decimals: 2 },
            { key: 'currentMargin', label: `${marginOrMarkupLabel} Atual`, type: 'number' as const, decimals: 4 },
            { key: 'currentSalesPrice', label: 'Preço Venda Atual', type: 'currency' as const, decimals: 2 },
            { key: 'cost', label: 'Custo Médio', type: 'currency' as const, decimals: 2 },
            { key: 'unitPrice', label: 'Preço Médio', type: 'currency' as const, decimals: 2 },
            { key: 'marginOrMarkup', label: `${marginOrMarkupLabel} das Vendas`, type: 'number' as const, decimals: 4 },
            { key: 'totalCost', label: 'Custo Total das Vendas', type: 'currency' as const, decimals: 2 },
            { key: 'total', label: 'Receita', type: 'currency' as const, decimals: 2 },
            { key: 'profit', label: 'Lucro', type: 'currency' as const, decimals: 2 }
        ];

        const totalsColumns = ['quantity', 'currentCostPrice', 'currentMargin', 'currentSalesPrice', 'cost', 'unitPrice', 'marginOrMarkup', 'totalCost', 'total', 'profit'];
        const calculations: { [key: string]: 'sum' | 'avg' | 'count' } = {
            quantity: 'sum',
            currentCostPrice: 'sum',
            currentMargin: 'sum',
            currentSalesPrice: 'sum',
            cost: 'avg',
            unitPrice: 'avg',
            marginOrMarkup: 'avg',
            totalCost: 'sum',
            total: 'sum',
            profit: 'sum',
        };

        const formatters = {
            productCode: ExcelExportService.textFormatter,
            name: ExcelExportService.textFormatter,
            quantity: (v: any) => v !== undefined && v !== null ? Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '',
            currentCostPrice: (v: any) => v !== undefined && v !== null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
            currentMargin: (v: any) => v !== undefined && v !== null ? `${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}%` : '',
            currentSalesPrice: (v: any) => v !== undefined && v !== null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
            cost: (v: any) => v !== undefined && v !== null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
            unitPrice: (v: any) => v !== undefined && v !== null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
            marginOrMarkup: (v: any) => v !== undefined && v !== null ? `${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}%` : '',
            totalCost: (v: any) => v !== undefined && v !== null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
            total: (v: any) => v !== undefined && v !== null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
            profit: (v: any) => v !== undefined && v !== null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
        };

        this.excelConfigService.exportReportToExcel(
            salesByProductData,
            fileName,
            headers,
            totalsColumns,
            formatters,
            calculations,
            'name',
            true  
        );
    }

    public exportSalesToExcel(fileName: string, salesData: any[]): void {
        const headers = [
            { key: 'code', label: 'Venda', type: 'number' as const },
            { key: 'pdvDescription', label: 'PDV', type: 'text' as const },
            { key: 'userName', label: 'Usuário', type: 'text' as const },
            { key: 'date', label: 'Data', type: 'date' as const },
            { key: 'subtotal', label: 'Subtotal', type: 'currency' as const },
            { key: 'discount', label: 'Desconto', type: 'currency' as const },
            { key: 'change', label: 'Troco', type: 'currency' as const },
            { key: 'total', label: 'Valor Total', type: 'currency' as const },
            { key: 'salesPaymentMethod', label: 'Formas de pagamento', type: 'text' as const },
        ];

        const totalsColumns = ['subtotal', 'discount', 'change', 'total'];
        const calculations: { [key: string]: 'sum' | 'avg' | 'count' } = {
            subtotal: 'sum',
            discount: 'sum',
            change: 'sum',
            total: 'sum',
        };

        const formatters = {
            pdvDescription: ExcelExportService.textFormatter,
            userName: ExcelExportService.textFormatter,
            subtotal: ExcelExportService.moneyFormatter,
            discount: ExcelExportService.moneyFormatter,
            change: ExcelExportService.moneyFormatter,
            total: ExcelExportService.moneyFormatter,
            salesPaymentMethod: (value: salesPaymentMethod[]) => {
                if (!value || value.length === 0) return '';
                return value
                    .map(payment => `${this.getPaymentMethodLabel(payment.paymentMethod)} - R$ ${ExcelExportService.moneyFormatter(payment.value)}`)
                    .join('; ');
            },
        };

        this.excelConfigService.exportReportToExcel(
            salesData,
            fileName,
            headers,
            totalsColumns,
            formatters,
            calculations,
            'code'
        );
    }

    private getPaymentMethodLabel(paymentMethod: number): string {
        return this.translateService.instant(`paymentMethods.${paymentMethod}`);
    }

    /**
     * Retorna 'Markup' ou 'Margem' conforme configuração dinâmica da empresa
     */
    public async getCurrentMarginOrMarkupLabel(): Promise<'Markup' | 'Margem'> {
        // Busca configurações já setadas para a empresa
        const configs = await this.wizardProductService.configurationSet();
        const priceFormationConfig = configs.find(c => c.configurationId === ConfigurationsEnum.PriceFormation);
        if (priceFormationConfig?.configurationOptionId === ConfigurationOptionsPriceFormationEnum.Markup) {
            return 'Markup';
        } else if (priceFormationConfig?.configurationOptionId === ConfigurationOptionsPriceFormationEnum.Margin) {
            return 'Margem';
        }
        // fallback padrão caso não encontrado
        return 'Markup';
    }
}
